import { useState, useEffect } from 'react';
import { api } from '@/App';
import { useTheme } from '@/contexts/ThemeContext';
import { toast } from 'sonner';
import { UserX, Shield, AlertTriangle, Key, Eye, Lock, CheckCircle, Clock, Filter, RefreshCw, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

export default function IdentityExposure() {
  const { isDarkMode } = useTheme();
  const [data, setData] = useState(null);
  const [remediations, setRemediations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [remediateDialogOpen, setRemediateDialogOpen] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [remediating, setRemediating] = useState(false);
  const [remediationAction, setRemediationAction] = useState('');
  const [remediationNotes, setRemediationNotes] = useState('');
  const [activeTab, setActiveTab] = useState('active');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [exposureRes, remediationsRes] = await Promise.all([
        api.get('/identity-exposure'),
        api.get('/identity-exposure/remediations').catch(() => ({ data: [] }))
      ]);
      setData(exposureRes.data);
      setRemediations(remediationsRes.data);
    } catch (error) {
      toast.error('Failed to fetch identity exposure data');
    } finally {
      setLoading(false);
    }
  };

  const runScan = async () => {
    setScanning(true);
    try {
      await fetchData();
      toast.success('Identity exposure scan completed');
    } catch (error) {
      toast.error('Scan failed');
    } finally {
      setScanning(false);
    }
  };

  const handleRemediateClick = (issue) => {
    setSelectedIssue(issue);
    setRemediationAction('');
    setRemediationNotes('');
    setRemediateDialogOpen(true);
  };

  const handleRemediate = async () => {
    if (!remediationAction) {
      toast.error('Please select an action taken');
      return;
    }

    setRemediating(true);
    try {
      await api.post('/identity-exposure/remediate', {
        issue_type: selectedIssue.type,
        user_email: selectedIssue.user_email,
        action_taken: remediationAction,
        notes: remediationNotes || null
      });
      toast.success('Issue marked as remediated');
      setRemediateDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to remediate issue');
    } finally {
      setRemediating(false);
    }
  };

  const getTypeIcon = (type) => {
    const icons = {
      mfa_gap: Lock,
      privileged_account: Key,
      credential_leak: AlertTriangle,
      weak_password: Shield,
      dormant_account: UserX
    };
    return icons[type] || Eye;
  };

  const getTypeLabel = (type) => {
    const labels = {
      mfa_gap: 'MFA Gap',
      privileged_account: 'Privileged Account',
      credential_leak: 'Credential Leak',
      weak_password: 'Weak Password',
      dormant_account: 'Dormant Account'
    };
    return labels[type] || type;
  };

  const getSeverityColor = (severity) => {
    const colors = {
      critical: 'bg-red-500/20 text-red-400 border-red-500/30',
      high: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
      medium: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      low: 'bg-green-500/20 text-green-400 border-green-500/30',
      info: 'bg-blue-500/20 text-blue-400 border-blue-500/30'
    };
    return colors[severity] || colors.info;
  };

  const getRemediationActions = (type) => {
    const actions = {
      mfa_gap: ['MFA Enabled', 'Exception Approved', 'User Notified'],
      privileged_account: ['Access Reviewed', 'Permissions Reduced', 'Account Monitored'],
      credential_leak: ['Password Reset', 'Account Locked', 'User Notified'],
      weak_password: ['Password Changed', 'Policy Enforced', 'User Training'],
      dormant_account: ['Account Disabled', 'Account Reviewed', 'Account Reactivated']
    };
    return actions[type] || ['Issue Resolved', 'Exception Approved', 'Under Review'];
  };

  const isRemediated = (issue) => {
    return remediations.some(r => 
      r.issue_type === issue.type && r.user_email === issue.user_email
    );
  };

  const activeIssues = data?.issues?.filter(issue => !isRemediated(issue)) || [];
  const remediatedIssues = data?.issues?.filter(issue => isRemediated(issue)) || [];

  if (loading && !scanning) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className={`w-8 h-8 animate-spin ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`} />
      </div>
    );
  }

  return (
    <div data-testid="identity-exposure-container" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`font-rajdhani font-bold text-4xl ${isDarkMode ? 'text-white text-shadow-neon' : 'text-slate-900'}`}>
            Identity Exposure Detection
          </h1>
          <div className="flex items-center gap-3 mt-1">
            <p className={`font-inter ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
              Monitor MFA gaps, privileged accounts, and credential exposure
            </p>
            {data?.scan_type === 'real' && (
              <span className={`text-xs px-2 py-0.5 rounded-full ${isDarkMode ? 'bg-emerald-500/20 text-emerald-400' : 'bg-emerald-100 text-emerald-700'}`}>
                REAL SCAN
              </span>
            )}
          </div>
        </div>
        <Button
          onClick={runScan}
          disabled={scanning}
          className="bg-indigo-600 hover:bg-indigo-500 text-white"
        >
          {scanning ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Scanning...
            </>
          ) : (
            <>
              <RefreshCw className="w-4 h-4 mr-2" />
              Scan Now
            </>
          )}
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <div className={`rounded-xl p-4 text-center ${isDarkMode ? 'glass' : 'bg-white border border-slate-200 shadow-sm'}`}>
          <p className={`text-2xl font-rajdhani font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{data?.total_issues || 0}</p>
          <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Total Issues</p>
        </div>
        <div className={`rounded-xl p-4 text-center border-l-2 border-red-500 ${isDarkMode ? 'glass' : 'bg-white border border-slate-200 shadow-sm'}`}>
          <p className="text-2xl font-rajdhani font-bold text-red-400">{data?.critical || 0}</p>
          <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Critical</p>
        </div>
        <div className={`rounded-xl p-4 text-center border-l-2 border-orange-500 ${isDarkMode ? 'glass' : 'bg-white border border-slate-200 shadow-sm'}`}>
          <p className="text-2xl font-rajdhani font-bold text-orange-400">{data?.high || 0}</p>
          <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>High</p>
        </div>
        <div className={`rounded-xl p-4 text-center border-l-2 border-yellow-500 ${isDarkMode ? 'glass' : 'bg-white border border-slate-200 shadow-sm'}`}>
          <p className="text-2xl font-rajdhani font-bold text-yellow-400">{data?.medium || 0}</p>
          <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Medium</p>
        </div>
        <div className={`rounded-xl p-4 text-center border-l-2 border-green-500 ${isDarkMode ? 'glass' : 'bg-white border border-slate-200 shadow-sm'}`}>
          <p className="text-2xl font-rajdhani font-bold text-green-400">{remediations.length}</p>
          <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Remediated</p>
        </div>
        <div className={`rounded-xl p-4 text-center border-l-2 border-blue-500 ${isDarkMode ? 'glass' : 'bg-white border border-slate-200 shadow-sm'}`}>
          <p className="text-2xl font-rajdhani font-bold text-blue-400">{activeIssues.length}</p>
          <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Active</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className={`${isDarkMode ? 'bg-zinc-900/50 border border-white/10' : 'bg-slate-100 border border-slate-200'}`}>
          <TabsTrigger 
            value="active" 
            className="data-[state=active]:bg-red-600/20 data-[state=active]:text-red-400"
          >
            <AlertTriangle className="w-4 h-4 mr-2" />
            Active Issues ({activeIssues.length})
          </TabsTrigger>
          <TabsTrigger 
            value="remediated" 
            className="data-[state=active]:bg-green-600/20 data-[state=active]:text-green-400"
          >
            <CheckCircle className="w-4 h-4 mr-2" />
            Remediated ({remediatedIssues.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="active" className="mt-5">
          <div className={`rounded-2xl p-6 ${isDarkMode ? 'glass' : 'bg-white border border-slate-200 shadow-sm'}`}>
            <h2 className={`font-rajdhani font-semibold text-xl mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Active Issues</h2>
            
            <div className="space-y-3">
              {activeIssues.length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <p className={`font-inter ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>All identity issues have been remediated!</p>
                </div>
              ) : (
                activeIssues.map((issue, idx) => {
                  const Icon = getTypeIcon(issue.type);
                  return (
                    <div 
                      key={idx}
                      data-testid={`identity-issue-${idx}`}
                      className={`p-4 rounded-xl border transition-all ${isDarkMode ? 'glass-hover border-white/10 hover:border-indigo-500/30' : 'bg-slate-50 border-slate-200 hover:border-indigo-300'}`}
                    >
                      <div className="flex items-start gap-4">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          issue.severity === 'critical' ? 'bg-red-500/20' :
                          issue.severity === 'high' ? 'bg-orange-500/20' :
                          issue.severity === 'medium' ? 'bg-yellow-500/20' : 'bg-blue-500/20'
                        }`}>
                          <Icon className={`w-5 h-5 ${
                            issue.severity === 'critical' ? 'text-red-400' :
                            issue.severity === 'high' ? 'text-orange-400' :
                            issue.severity === 'medium' ? 'text-yellow-400' : 'text-blue-400'
                          }`} />
                        </div>
                        
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-1">
                            <span className={`px-2 py-0.5 rounded text-xs uppercase border ${getSeverityColor(issue.severity)}`}>
                              {issue.severity}
                            </span>
                            <span className={`text-xs font-mono ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>{getTypeLabel(issue.type)}</span>
                          </div>
                          
                          <p className={`font-inter text-sm mb-1 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{issue.description}</p>
                          
                          {issue.user_email && (
                            <p className={`text-xs font-mono ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>{issue.user_email}</p>
                          )}
                          
                          {issue.recommendation && (
                            <p className="text-indigo-400 text-xs mt-2">
                              Recommendation: {issue.recommendation}
                            </p>
                          )}
                        </div>
                        
                        <Button 
                          size="sm" 
                          className="bg-emerald-600 hover:bg-emerald-500"
                          onClick={() => handleRemediateClick(issue)}
                          data-testid={`remediate-btn-${idx}`}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Remediate
                        </Button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="remediated" className="mt-5">
          <div className={`rounded-2xl p-6 ${isDarkMode ? 'glass' : 'bg-white border border-slate-200 shadow-sm'}`}>
            <h2 className={`font-rajdhani font-semibold text-xl mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Remediation History</h2>
            
            <div className="space-y-3">
              {remediations.length === 0 ? (
                <div className="text-center py-12">
                  <Clock className={`w-16 h-16 mx-auto mb-4 ${isDarkMode ? 'text-zinc-600' : 'text-slate-400'}`} />
                  <p className={`font-inter ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>No remediation history yet</p>
                </div>
              ) : (
                remediations.map((remediation, idx) => (
                  <div 
                    key={idx}
                    className={`p-4 rounded-xl border ${isDarkMode ? 'glass-hover border-green-500/20 bg-green-500/5' : 'bg-green-50 border-green-200'}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 bg-green-500/20">
                        <CheckCircle className="w-5 h-5 text-green-400" />
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-1">
                          <span className="text-green-400 text-xs font-mono uppercase">{getTypeLabel(remediation.issue_type)}</span>
                          <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-400'}`}>•</span>
                          <span className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>{remediation.user_email}</span>
                        </div>
                        
                        <p className={`font-inter text-sm mb-1 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                          Action: <span className="text-emerald-400">{remediation.action_taken}</span>
                        </p>
                        
                        {remediation.notes && (
                          <p className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Notes: {remediation.notes}</p>
                        )}
                        
                        <div className={`flex items-center gap-4 mt-2 text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                          <span>By: {remediation.remediated_by_name}</span>
                          <span>•</span>
                          <span>{new Date(remediation.remediated_at).toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Remediation Dialog */}
      <Dialog open={remediateDialogOpen} onOpenChange={setRemediateDialogOpen}>
        <DialogContent className={`${isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : 'bg-white border-slate-200 text-slate-900'}`}>
          <DialogHeader>
            <DialogTitle className="font-rajdhani text-2xl flex items-center gap-2">
              <CheckCircle className="w-6 h-6 text-emerald-400" />
              Remediate Issue
            </DialogTitle>
          </DialogHeader>
          
          {selectedIssue && (
            <div className="space-y-4 mt-4">
              <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-100'}`}>
                <p className={`text-xs mb-1 ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Issue Type</p>
                <p className={`font-mono ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{getTypeLabel(selectedIssue.type)}</p>
              </div>
              
              <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-100'}`}>
                <p className={`text-xs mb-1 ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Affected User</p>
                <p className={`font-mono ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{selectedIssue.user_email}</p>
              </div>
              
              <div>
                <Label className={isDarkMode ? 'text-zinc-300' : 'text-slate-700'}>Action Taken</Label>
                <Select value={remediationAction} onValueChange={setRemediationAction}>
                  <SelectTrigger className={`mt-2 ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'}`}>
                    <SelectValue placeholder="Select action taken..." />
                  </SelectTrigger>
                  <SelectContent className={`${isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : 'bg-white border-slate-200 text-slate-900'}`}>
                    {getRemediationActions(selectedIssue.type).map(action => (
                      <SelectItem key={action} value={action}>{action}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className={isDarkMode ? 'text-zinc-300' : 'text-slate-700'}>Notes (Optional)</Label>
                <textarea
                  value={remediationNotes}
                  onChange={(e) => setRemediationNotes(e.target.value)}
                  className={`w-full rounded-md p-3 mt-2 min-h-[80px] ${isDarkMode ? 'bg-zinc-950/50 border border-white/10 text-white' : 'bg-white border border-slate-300 text-slate-900'}`}
                  placeholder="Add any additional notes..."
                />
              </div>
              
              <Button
                onClick={handleRemediate}
                disabled={remediating || !remediationAction}
                className="w-full bg-emerald-600 hover:bg-emerald-500"
                data-testid="confirm-remediate-btn"
              >
                {remediating ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Processing...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Mark as Remediated
                  </>
                )}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
